import { Logger, ClientsideLogger } from '../logger/logger';
import { dePerl, isCustomEventSupported, UnauthorizedError, ForbiddenError, JSONParseError, FailedRequestError, NoStatusCodeError } from './util';
import { promiseWithTimeout } from '../utils/promiseWithTimeout';
import { Endpoints, Debug } from './services';
import { OperationType, DEFAULT_HTTP_TIMEOUT, DEFAULT_WIDGET_TIMEOUT, MAX_HTTP_TIMEOUT } from './request';
import { AuthMode } from './WPSClient';

const DEFAULT_HEADERS: HeadersInit = global.__SERVER__
    ? {
        'content-encoding': 'amz-1.0',
        'content-type': 'application/json; charset=UTF-8',
    } : {
        'x-requested-with': 'XMLHttpRequest'
    };
const DEFAULT_OPTS = global.__SERVER__
    ? {
        requester: 'APIClient',
        operationType: OperationType.WRITE
    }
    : {
        requester: 'APIClient',
        operationType: OperationType.WRITE,
        credentials: 'same-origin'
    } as RequestInit & { requester: string, operationType: OperationType }
const RETRY_DELAY = 200; // in milliseconds

export interface APIOpts {
    name: string,
    requester: string,
    operationType: OperationType,
    timeout?: number,
    retryable?: boolean,
    agent?: any,
};

export interface AccessToken {
    tokenType: string,
    token: string,
};

export interface AccessTokenInput {
    host: string,
    realm: string,
    scope: string,
};

export interface CloudauthClient {
    getAccessToken: (input: AccessTokenInput) => Promise<AccessToken>,
};

export interface Authentication {
    authMode: AuthMode,
    cloudauthClient: CloudauthClient,
};

export class APIClient {
    widgetTimeout: number;
    debugs: Array<Debug & { request: unknown, response: unknown }>;
    constructor(public logger: Logger = new ClientsideLogger(), public endpoints = ({} as Partial<Endpoints>), widgetTimeout?: number, public authentication?: Authentication, public agent?: any) {
        this.debugs = [];
        this.widgetTimeout = widgetTimeout || DEFAULT_WIDGET_TIMEOUT;
    }

    // Debug data for the Inspector ™
    private attachDebug(id: unknown, name: string, requestJson: any, responseJson: { debug: Debug }) {
        const clonedResponse = JSON.parse(JSON.stringify(responseJson)) as { debug: Debug };
        const { debug, ...response } = clonedResponse
        if (global.__SERVER__) {
            this.debugs.push({ ...debug, operationName: name, request: requestJson, response });
        } else if (isCustomEventSupported()) {
            window.dispatchEvent(new CustomEvent('avInspectorWidgetAjaxSuccess', { detail: { id, operationName: name, debug, response } }));
        }
    }

    private async callOnce(
        url: string,
        opts: RequestInit & APIOpts
    ): Promise<any> {
        this.logger.info(`Sending request to ${url}`);

        const {
            name,
            operationType,
            timeout,
            retryable,
            // requester is not extracted from requestInit to allow inspector to intercept.
            ...requestInit
        } = opts

        return fetch(url, requestInit).then(async (response) => {
            // Non 200 status code
            if (!response.ok) {
                this.logger.error(`Status code: ${response.status} received from ${url}`);
                const text = await response.text();
                this.logger.error('Received response:', text);

                // Do not add new Exception types without looking shouldRetry() below
                switch(response.status) {
                    case 0:
                        throw new NoStatusCodeError(text);
                    case 401:
                        throw new UnauthorizedError(text);
                    case 403:
                        throw new ForbiddenError(text);
                    default:
                        throw new FailedRequestError(text);
                }
            }

            // [200 - 299] status code
            try {
                return global.__SERVER__
                    ? await response.json()
                    : await response.json().then(dePerl);
            } catch (err) {
                this.logger.error('Could not parse JSON response').error(err);
                throw new JSONParseError(err.message);
            }
        })
    }

    async call(
        url: string | undefined,
        partialOpts: Partial<RequestInit & APIOpts> = {}
    ): Promise<any> {
        const start = Date.now();
        const id = start;
        const timeout = Math.min(
            partialOpts.timeout || DEFAULT_HTTP_TIMEOUT,
            this.widgetTimeout,
            MAX_HTTP_TIMEOUT
        );
        const opts: RequestInit & APIOpts = {
            ...DEFAULT_OPTS,
            name: url || 'APIClient',
            ...partialOpts,
            headers: {
                ...DEFAULT_HEADERS,
                ...(partialOpts.headers || {}),
                'X-Amzn-Client-TTL-Seconds': (timeout / 1000).toString()
            }
        };

        if (global.__DEVSERVER__) {
            opts.headers = { ...opts.headers!, 'x-proxy-url': url || '' }
            url = '/gp/video/devProxy/';
        }

        if (!url) {
            throw new Error(`${opts.requester} failed to specify url when calling ${opts.name}`);
        }

        if (!global.__SERVER__ && isCustomEventSupported()) {
            window.dispatchEvent(new CustomEvent('avInspectorWidgetAjaxStart', { detail: { id, operationName: opts.name } }));
        }

        return await promiseWithTimeout<any>(
            this.callOnce(url, opts).catch(async (e) => {
                if (this.shouldRetry(e, opts.retryable || false)) {
                    await new Promise((resolve) => { setTimeout(resolve, RETRY_DELAY) });
                    this.logger.count('DVWebNodeAPIClientRetries', 1, true);

                    const timePassed = Date.now() - start;
                    opts.headers = {
                        ...opts.headers,
                        'X-Amzn-Client-TTL-Seconds': ((timeout - timePassed) / 1000).toString()
                    }
                    return await this.callOnce(url!, opts);
                } else {
                    throw e;
                }
            }),
            timeout
        ).then((json) => {
            if (json.hasOwnProperty('debug')) {
                this.attachDebug(id, opts.name, json.debug.request || opts.body, json);
            }
            return json;
        });
    }

    private shouldRetry(exception: any, retryable: boolean): boolean {
        if(exception && retryable) {
            if (!global.__SERVER__ && exception instanceof Error) {
                // Client-Side only retries
                return !(exception instanceof FailedRequestError && exception instanceof JSONParseError);
            }
            
            return exception.code === 'ECONNRESET' ||
                exception instanceof UnauthorizedError ||
                exception instanceof ForbiddenError ||
                exception instanceof NoStatusCodeError;
        }
        return false;
    }
}
